import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

// ─── Scroll-fade hook ──────────────────────────────────────────────────────────
function useFadeIn(delay = 0) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let timer;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          timer = setTimeout(() => setVisible(true), delay);
          observer.unobserve(el);
        }
      },
      { threshold: 0.06, rootMargin: '0px 0px -24px 0px' }
    );
    observer.observe(el);
    return () => { observer.unobserve(el); clearTimeout(timer); };
  }, [delay]);
  return [ref, visible];
}

// ─── Icons ─────────────────────────────────────────────────────────────────────
const S = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round' };

function IconArrow({ className = 'w-4 h-4' }) {
  return <svg viewBox="0 0 24 24" className={className} {...S}><path d="M5 12h14M12 5l7 7-7 7" /></svg>;
}
function IconCheck({ className = 'w-3 h-3' }) {
  return <svg viewBox="0 0 24 24" className={className} {...S}><path d="M20 6 9 17l-5-5" /></svg>;
}
function IconCheckCircle() {
  return <svg viewBox="0 0 24 24" className="w-6 h-6" {...S}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>;
}
function IconChevron({ open }) {
  return (
    <svg viewBox="0 0 24 24" className={`w-4 h-4 transition-transform duration-300 ${open ? 'rotate-180' : ''}`} {...S}>
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}
function IconClock({ className = 'w-6 h-6' }) {
  return <svg viewBox="0 0 24 24" className={className} {...S}><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>;
}
function IconHeart({ className = 'w-6 h-6' }) {
  return <svg viewBox="0 0 24 24" className={className} {...S}><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" /></svg>;
}
function IconPlay({ className = 'w-6 h-6' }) {
  return <svg viewBox="0 0 24 24" className={className} {...S}><circle cx="12" cy="12" r="10" /><polygon points="10 8 16 12 10 16 10 8" /></svg>;
}
function IconBook({ className = 'w-6 h-6' }) {
  return <svg viewBox="0 0 24 24" className={className} {...S}><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" /></svg>;
}
function IconShieldCheck({ className = 'w-6 h-6' }) {
  return <svg viewBox="0 0 24 24" className={className} {...S}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="m9 12 2 2 4-4" /></svg>;
}
function IconGlobe({ className = 'w-6 h-6' }) {
  return <svg viewBox="0 0 24 24" className={className} {...S}><circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></svg>;
}
function IconRepeat({ className = 'w-6 h-6' }) {
  return <svg viewBox="0 0 24 24" className={className} {...S}><polyline points="17 1 21 5 17 9" /><path d="M3 11V9a4 4 0 0 1 4-4h14" /><polyline points="7 23 3 19 7 15" /><path d="M21 13v2a4 4 0 0 1-4 4H3" /></svg>;
}
function IconTrendingUp({ className = 'w-6 h-6' }) {
  return <svg viewBox="0 0 24 24" className={className} {...S}><polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" /></svg>;
}

// ─── Data ──────────────────────────────────────────────────────────────────────
const PAIN_POINTS = [
  {
    Icon: IconClock,
    title: "First-time visitors can't find you",
    desc: "Most people research churches online before visiting. Without a clear, welcoming website, they quietly move on, and never walk through your doors.",
  },
  {
    Icon: IconHeart,
    title: "You're missing digital giving every week",
    desc: "63% of donors prefer to give digitally. Without online giving, you leave significant contributions uncollected, every single Sunday.",
  },
  {
    Icon: IconBook,
    title: "Your message ends when the service does",
    desc: "People want to revisit sermons, share them with friends, or catch what they missed. Without an archive, that impact disappears the moment you close your Bible.",
  },
  {
    Icon: IconGlobe,
    title: "There's no way to reach the homebound",
    desc: "Illness, travel, and life keep members from attending. Without live streaming, they're completely cut off from the congregation and the Word.",
  },
];

const CHURCH_FEATURES = [
  {
    Icon: IconClock,
    tag: 'Never miss a visitor',
    title: 'Service Times & Location',
    desc: 'Clear, always current service times and directions displayed prominently on your site. New visitors know exactly when and where to show up before they ever leave home.',
    color: 'emerald',
  },
  {
    Icon: IconShieldCheck,
    tag: 'Attract the right members',
    title: 'Statement of Faith',
    desc: "Your core beliefs and doctrine, clearly and beautifully presented. The right people find your congregation because they already share your convictions.",
    color: 'amber',
  },
  {
    Icon: IconHeart,
    tag: 'Give from any device, any time',
    title: 'Online Giving',
    desc: "A dedicated giving page that links directly to your church's existing giving methods. Cash App, Venmo, PayPal, or any platform you already use. Easy to find, easy to use from any device.",
    color: 'emerald',
  },
  {
    Icon: IconPlay,
    tag: 'Reach anyone, anywhere',
    title: 'Live Stream',
    desc: 'A polished live stream page integrated with YouTube Live, Facebook Live, or your preferred platform. Reach members who are traveling, homebound, or simply curious about your church before their first visit.',
    color: 'amber',
  },
];

const PROJECTS = [
  {
    title: 'Example First Draft — Grace Community Church',
    desc: 'Walk through it like a client would. See the layout, the features, the feel — then tell us what you\'d change. This is the starting point, not the finish line.',
    image: '/gracecommunity.webp',
    url: 'https://churchdemo90.netlify.app',
  },
  // {
  //   title: 'Full Stack Web Application',
  //   desc: 'A feature rich web platform with user accounts, role based access, and integrated file management. Built and shipped in 6 weeks.',
  //   image: '/project2.webp',
  // },
  // {
  //   title: 'React Native Mobile App',
  //   desc: 'A cross platform mobile app backed by a real database. Designed for accessibility and shipped to the App Store.',
  //   image: '/project3.webp',
  // },
];

const PROCESS_STEPS = [
  {
    step: 'Step 1',
    title: 'Discovery Call',
    time: '',
    desc: "We learn about your congregation, your mission, and your community through a short questionnaire and a quick call. You get a clear scope, timeline, and price upfront. After that, you don't lift a finger.",
  },
  {
    step: 'Step 2',
    title: 'Design & Build',
    time: '',
    desc: "We write the copy, design the site, and build every page to reflect your church's identity and values. You review at every stage and request changes freely. We don't move forward until you're happy with every detail.",
  },
  {
    step: 'Step 3',
    title: 'Launch & Handoff',
    time: '',
    desc: "We test everything, connect your domain, and go live. It's that simple.",
  },
];

const FAQ_ITEMS = [
  {
    q: 'Can our members give tithes and offerings online?',
    a: "Yes. We build a dedicated giving page that links directly to your church's existing giving methods. Cash App, Venmo, PayPal, or whatever your congregation already uses. No new payment setup required on your end.",
  },
  {
    q: 'How does the live stream work?',
    a: "We integrate your site with YouTube Live, Facebook Live, or your preferred streaming platform. Your stream page looks polished and professional. Visitors can watch without ever leaving your website.",
  },
  {
    q: 'Will our church be findable on Google?',
    a: "Yes. Every page includes SEO foundations including proper titles, descriptions, and local schema markup so people searching for a church in your area have a real chance of finding you.",
  },
  {
    q: 'Do you work with churches of all sizes?',
    a: "Yes, from small congregations of 30 to multi campus ministries. We scope the project to fit what you need, so you're never paying for features your church won't use.",
  },
];

const GUARANTEES = [
  {
    Icon: IconRepeat,
    title: 'Love It or We Refund You',
    desc: "If the finished site doesn't meet your expectations, we'll refund you in full. No arguments, no hard feelings. We build every site like our reputation depends on it — because it does.",
  },
  {
    Icon: IconShieldCheck,
    title: 'Completely Hands Free',
    desc: "We write the copy, design every page, build it, and launch it. You answer a few questions about your church and we handle everything else. Not a word or a line of code required from you.",
  },
  {
    Icon: IconTrendingUp,
    title: 'No Surprise Fees. Ever.',
    desc: "The price we quote is the price you pay. Scope changes are discussed and agreed on before any extra work begins.",
  },
];

const CONGREGATION_SIZES = ['Under 100 members', '100 to 500 members', '500 to 1,500 members', '1,500+ members'];

// ─── Component ─────────────────────────────────────────────────────────────────
const HERO_IMAGES = ['/project1.webp', '/project2.webp', '/project3.webp'];
const SLIDE_INTERVAL = 8000;

export default function ChurchFunnel() {
  const [openFaq, setOpenFaq] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [activeHeroBg, setActiveHeroBg] = useState(0);
  const [showSticky, setShowSticky] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [error, setError] = useState(null);
  const [congSize, setCongSize] = useState('');
  const [reviewChurchName, setReviewChurchName] = useState('');
  const [reviewName, setReviewName] = useState('');
  const [reviewEmail, setReviewEmail] = useState('');
  const [reviewUrl, setReviewUrl] = useState('');
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);

  useEffect(() => {
    const update = () => {
      setScrolled(window.scrollY > 40);
      setShowSticky(window.scrollY > 700);
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    return () => window.removeEventListener('scroll', update);
  }, []);

  useEffect(() => {
    const id = setInterval(
      () => setActiveHeroBg((p) => (p + 1) % HERO_IMAGES.length),
      SLIDE_INTERVAL,
    );
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!showThankYou) return;
    const id = setTimeout(() => setShowThankYou(false), 4500);
    return () => clearTimeout(id);
  }, [showThankYou]);

  const [painRef, painVisible] = useFadeIn();
  const [featRef, featVisible] = useFadeIn();
  const [workRef, workVisible] = useFadeIn();
  const [procRef, procVisible] = useFadeIn();
  const [priceRef, priceVisible] = useFadeIn();
  const [faqRef, faqVisible] = useFadeIn();
  const [guarRef, guarVisible] = useFadeIn();
  const [ctaRef, ctaVisible] = useFadeIn();
  const [formRef, formVisible] = useFadeIn();
  const [reviewRef, reviewVisible] = useFadeIn();

  const fc = (v) =>
    `transition-all duration-700 ease-out ${v ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`;

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    setReviewLoading(true);
    const fd = new FormData();
    fd.append('access_key', process.env.REACT_APP_WEB3FORMS_KEY);
    fd.append('subject', 'Free Website Review Request – Church');
    fd.append('church_name', reviewChurchName);
    fd.append('name', reviewName);
    fd.append('email', reviewEmail);
    fd.append('website_url', reviewUrl);
    try {
      await fetch('https://api.web3forms.com/submit', { method: 'POST', body: fd });
    } catch {}
    setReviewLoading(false);
    setReviewSubmitted(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    const form = e.target;
    const fd = new FormData(form);
    fd.append('access_key', process.env.REACT_APP_WEB3FORMS_KEY);
    try {
      const res = await fetch('https://api.web3forms.com/submit', { method: 'POST', body: fd });
      if (!res.ok) throw new Error();
      const data = await res.json();
      if (data.success) { form.reset(); setCongSize(''); setShowThankYou(true); }
      else setError('Something went wrong. Please try again.');
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-full overflow-x-hidden bg-white text-slate-900 selection:bg-amber-200/50">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <header
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${scrolled
          ? 'bg-white/80 border-b border-black/[0.07] shadow-sm shadow-black/[0.04]'
          : 'bg-transparent'
          }`}
        style={{ backdropFilter: 'saturate(180%) blur(20px)', WebkitBackdropFilter: 'saturate(180%) blur(20px)' }}
      >
        <div className="mx-auto max-w-6xl px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <svg viewBox="0 0 512 512"
              className={`h-9 w-9 transition-colors duration-500 ${scrolled ? 'text-slate-900' : 'text-white'}`}
              fill="none" stroke="currentColor" strokeWidth={38} strokeLinecap="round" strokeLinejoin="round">
              <g transform="translate(256,256)">
                <circle cx="0" cy="0" r="140" />
                <path d="M -50 105 L -50 -105" />
                <path d="M -50 -105 L 10 -105 A 60 60 0 0 1 10 15 L -50 15" />
              </g>
            </svg>
            <span className={`text-lg font-semibold tracking-tight transition-colors duration-500 ${scrolled ? 'text-slate-900' : 'text-white'}`}>
              Peronyx
            </span>
          </Link>

          <div className="flex items-center gap-3">
            <a href="#pricing"
              className={`hidden md:inline-block text-[13px] font-medium transition-colors duration-300 ${scrolled ? 'text-slate-500 hover:text-slate-900' : 'text-white/70 hover:text-white'}`}>
              Pricing
            </a>
            <a href="#quote"
              className={`inline-flex items-center gap-1.5 rounded-xl px-5 py-2 text-[13px] font-semibold transition-all duration-300 ${scrolled
                ? 'bg-amber-500 text-white hover:bg-amber-600 shadow-sm shadow-amber-900/20'
                : 'bg-white/[0.12] border border-white/20 text-white hover:bg-white/20'
                }`}>
              Free Quote
              <IconArrow className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      </header>

      {/* ── Hero ───────────────────────────────────────────────────────────── */}
      <section id="hero" className="relative min-h-screen flex items-center overflow-hidden bg-slate-950">
        {HERO_IMAGES.map((src, i) => (
          <img
            key={src}
            src={src}
            alt=""
            role="presentation"
            className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-[2500ms] ease-out ${
              i === activeHeroBg ? 'opacity-100' : 'opacity-0'
            }`}
          />
        ))}
        <div className="absolute inset-0 bg-slate-950/80" />
        <div className="absolute inset-0 hero-color-drift" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/65 via-slate-950/20 to-transparent" />
        {/* Warm accent blob */}
        <div className="hero-blob absolute -top-24 left-1/3 h-[30rem] w-[42rem] rounded-full blur-3xl bg-gradient-to-tr from-amber-500/20 to-emerald-500/15 pointer-events-none" />
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-slate-950 to-transparent pointer-events-none" />

        <div className="relative z-10 mx-auto max-w-6xl px-6 pt-32 pb-24 w-full">
          <div className="max-w-3xl">
            <div className="hero-fade-up inline-flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/10 px-4 py-1.5 mb-8" style={{ animationDelay: '60ms' }}>
              <span className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-pulse" />
              <span className="text-[11px] font-semibold tracking-[0.22em] text-amber-400 uppercase">
                Church Website Design & Development
              </span>
            </div>

            <h1 className="text-5xl sm:text-6xl md:text-[5rem] font-extrabold tracking-tight leading-[1.02] text-white">
              <span className="word-rise block" style={{ animationDelay: '180ms' }}>A better online home</span>
              <span className="word-rise-breathe block mt-1">
                <span className="shimmer-text">For your church.</span>
              </span>
            </h1>

            <p className="hero-fade-up mt-8 max-w-xl text-slate-300/90 text-lg leading-relaxed" style={{ animationDelay: '780ms' }}>
              We design, write, and build your entire church website. You answer a few questions and we handle the copy, the design, the code, and the launch. Completely hands free.
            </p>

            <div className="hero-fade-up mt-10 flex flex-wrap gap-3" style={{ animationDelay: '920ms' }}>
              <a href="#quote"
                className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-7 py-3.5 text-white text-sm font-semibold hover:bg-amber-600 active:scale-[0.97] transition-all duration-150 shadow-xl shadow-amber-950/50">
                Get a Free Quote
                <IconArrow />
              </a>
              <a href="#features"
                className="inline-flex items-center gap-2 rounded-xl border border-white/15 px-7 py-3.5 text-white/85 text-sm font-medium hover:bg-white/[0.08] hover:text-white active:scale-[0.97] transition-all duration-150">
                See What's Included
              </a>
            </div>

            {/* Trust pills */}
            <div className="hero-fade-up mt-12 flex flex-wrap gap-2.5" style={{ animationDelay: '1080ms' }}>
              {['Weeks, Not Months', 'Online Giving Built-In', 'Live Stream Ready', 'Statement of Faith'].map((pill) => (
                <span key={pill}
                  className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.10] bg-white/[0.05] px-3.5 py-1.5 text-xs font-medium text-slate-400">
                  <span className="h-1 w-1 rounded-full bg-amber-400/80 flex-shrink-0" />
                  {pill}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-10">
          {HERO_IMAGES.map((_, i) => (
            <button
              key={i}
              onClick={() => setActiveHeroBg(i)}
              aria-label={`Show slide ${i + 1}`}
              className={`h-1 rounded-full transition-all duration-500 ${
                i === activeHeroBg ? 'w-8 bg-amber-400' : 'w-2 bg-white/25 hover:bg-white/50'
              }`}
            />
          ))}
        </div>
      </section>

      {/* ── Trust bar ──────────────────────────────────────────────────────── */}
      <div className="border-b border-slate-100">
        <div className="mx-auto max-w-6xl px-6 py-8 grid grid-cols-2 md:grid-cols-4 gap-6 md:divide-x md:divide-slate-100">
          {[
            { value: 'Fast Delivery', label: 'Weeks, not months' },
            { value: 'Online Giving', label: 'Ready on launch day' },
            { value: 'Live Stream', label: 'Integrated from day one' },
            { value: 'Done For You', label: 'We handle everything' },
          ].map(({ value, label }) => (
            <div key={value} className="text-center md:px-6">
              <div className="text-base md:text-lg font-extrabold tracking-tight text-slate-900 leading-tight">{value}</div>
              <div className="mt-1 text-[11px] text-slate-500 font-medium leading-snug">{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Pain section ───────────────────────────────────────────────────── */}
      <section className="py-28 bg-[#f5f5f7]">
        <div ref={painRef} className={`mx-auto max-w-6xl px-6 ${fc(painVisible)}`}>
          <div className="text-center mb-14">
            <p className="text-xs font-semibold tracking-[0.22em] text-amber-600 uppercase mb-3">Sound familiar?</p>
            <h2 className="text-3xl md:text-[2.75rem] font-bold tracking-tight leading-[1.12]">
              Your congregation deserves<br className="hidden md:block" /> better than a forgotten website.
            </h2>
            <p className="mt-4 text-slate-500 max-w-lg mx-auto text-base leading-relaxed">
              A weak online presence doesn't just fail to help, it actively turns people
              away from your door before they ever arrive.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {PAIN_POINTS.map(({ Icon, title, desc }) => (
              <div key={title}
                className="group bg-white rounded-3xl p-7 shadow-sm ring-1 ring-black/[0.04] hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                <div className="h-12 w-12 rounded-2xl bg-amber-50 ring-1 ring-amber-100/80 grid place-items-center text-amber-500 mb-6">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="text-[15px] font-bold tracking-tight text-slate-900 mb-2.5 leading-snug">{title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <a href="#quote"
              className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-7 py-3.5 text-sm font-semibold text-white hover:bg-amber-600 active:scale-[0.97] transition-all duration-150 shadow-md shadow-amber-900/15">
              Build a site that serves your congregation
              <IconArrow />
            </a>
          </div>
        </div>
      </section>

      {/* ── Free Website Review ────────────────────────────────────────────── */}
      <section className="py-20 bg-slate-950 text-white">
        <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 h-64 w-[32rem] blur-3xl opacity-[0.07] bg-amber-500 rounded-full" />
        <div ref={reviewRef} className={`relative mx-auto max-w-lg px-6 text-center ${fc(reviewVisible)}`}>
          <p className="text-sm font-semibold tracking-[0.22em] text-amber-400 uppercase mb-3" style={{ textShadow: '0 0 8px rgba(251, 191, 36, 0.35)' }}>Free offer</p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
            Get a free review of your current site
          </h2>
          <p className="text-slate-400 text-base leading-relaxed mb-10">
            Drop your URL below and we'll dig into your site, identify what's holding your church
            back online, and send you a clear honest review — <span className="text-amber-400" style={{ textShadow: '0 0 8px rgba(251, 191, 36, 0.35)' }}>Completely free, no strings attached</span>.
          </p>
          {!reviewSubmitted ? (
            <form onSubmit={handleReviewSubmit} className="space-y-4 text-left">
              <label className="block">
                <span className="text-[10px] font-bold tracking-widest text-slate-500 uppercase">Church Name</span>
                <input required value={reviewChurchName} onChange={e => setReviewChurchName(e.target.value)}
                  placeholder="Your church name" autoComplete="organization"
                  className="mt-2 w-full rounded-xl bg-white/[0.05] border border-white/[0.09] px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-amber-500/70 focus:ring-1 focus:ring-amber-500/60 transition-all" />
              </label>
              <div className="grid sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-[10px] font-bold tracking-widest text-slate-500 uppercase">Name</span>
                  <input required value={reviewName} onChange={e => setReviewName(e.target.value)}
                    placeholder="Your name" autoComplete="name"
                    className="mt-2 w-full rounded-xl bg-white/[0.05] border border-white/[0.09] px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-amber-500/70 focus:ring-1 focus:ring-amber-500/60 transition-all" />
                </label>
                <label className="block">
                  <span className="text-[10px] font-bold tracking-widest text-slate-500 uppercase">Email</span>
                  <input required type="email" value={reviewEmail} onChange={e => setReviewEmail(e.target.value)}
                    placeholder="you@church.org" autoComplete="email"
                    className="mt-2 w-full rounded-xl bg-white/[0.05] border border-white/[0.09] px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-amber-500/70 focus:ring-1 focus:ring-amber-500/60 transition-all" />
                </label>
              </div>
              <label className="block">
                <span className="text-[10px] font-bold tracking-widest text-slate-500 uppercase">Your current website</span>
                <input required type="text" value={reviewUrl} onChange={e => setReviewUrl(e.target.value)}
                  placeholder="yourchurch.org" autoComplete="url"
                  className="mt-2 w-full rounded-xl bg-white/[0.05] border border-white/[0.09] px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-amber-500/70 focus:ring-1 focus:ring-amber-500/60 transition-all" />
              </label>
              <button type="submit" disabled={reviewLoading}
                className="w-full rounded-xl bg-amber-500 px-5 py-3.5 text-sm font-semibold text-white hover:bg-amber-600 active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-150 shadow-lg shadow-amber-900/30">
                {reviewLoading ? 'Sending…' : 'Send Me My Free Review →'}
              </button>
            </form>
          ) : (
            <div className="py-10 text-amber-400 font-semibold text-lg">
              Thank you! We will review your site and send you what we find.
            </div>
          )}
        </div>
      </section>

      {/* ── Church Feature Highlights ───────────────────────────────────────── */}
      <section id="features" className="py-28 bg-white">
        <div ref={featRef} className={`mx-auto max-w-6xl px-6 ${fc(featVisible)}`}>
          <div className="text-center mb-16">
            <p className="text-xs font-semibold tracking-[0.22em] text-amber-600 uppercase mb-3">What's included</p>
            <h2 className="text-3xl md:text-[2.75rem] font-bold tracking-tight leading-[1.12]">
              Every page your church needs,<br className="hidden md:block" /> built in from day one.
            </h2>
            <p className="mt-4 text-slate-500 max-w-lg mx-auto text-base leading-relaxed">
              Not add-ons. Not integrations you have to figure out later. These features
              are designed into your site from the start.
            </p>
          </div>

          {/* Top 3 */}
          <div className="grid md:grid-cols-3 gap-5 mb-5">
            {CHURCH_FEATURES.slice(0, 3).map(({ Icon, tag, title, desc, color }) => (
              <div key={title}
                className="rounded-3xl bg-slate-50 ring-1 ring-slate-100 p-8 hover:shadow-md hover:-translate-y-0.5 transition-all duration-300">
                <div className={`mb-5 h-12 w-12 rounded-2xl grid place-items-center ${color === 'amber'
                  ? 'bg-amber-100 ring-1 ring-amber-200/60 text-amber-600'
                  : 'bg-emerald-100 ring-1 ring-emerald-200/60 text-emerald-600'
                  }`}>
                  <Icon className="w-5 h-5" />
                </div>
                <p className={`text-[10px] font-bold tracking-[0.22em] uppercase mb-2 ${color === 'amber' ? 'text-amber-500' : 'text-emerald-600'}`}>
                  {tag}
                </p>
                <h3 className="text-lg font-bold tracking-tight text-slate-900 mb-3">{title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>

          {/* Bottom 1 — centered */}
          <div className="max-w-sm mx-auto">
            {CHURCH_FEATURES.slice(3).map(({ Icon, tag, title, desc, color }) => (
              <div key={title}
                className="rounded-3xl bg-slate-50 ring-1 ring-slate-100 p-8 hover:shadow-md hover:-translate-y-0.5 transition-all duration-300">
                <div className={`mb-5 h-12 w-12 rounded-2xl grid place-items-center ${color === 'amber'
                  ? 'bg-amber-100 ring-1 ring-amber-200/60 text-amber-600'
                  : 'bg-emerald-100 ring-1 ring-emerald-200/60 text-emerald-600'
                  }`}>
                  <Icon className="w-5 h-5" />
                </div>
                <p className={`text-[10px] font-bold tracking-[0.22em] uppercase mb-2 ${color === 'amber' ? 'text-amber-500' : 'text-emerald-600'}`}>
                  {tag}
                </p>
                <h3 className="text-lg font-bold tracking-tight text-slate-900 mb-3">{title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <a href="#quote"
              className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-7 py-3.5 text-sm font-semibold text-white hover:bg-amber-600 active:scale-[0.97] transition-all duration-150 shadow-md shadow-amber-900/15">
              Get a free quote for your church
              <IconArrow />
            </a>
          </div>
        </div>
      </section>

      {/* ── Work samples ───────────────────────────────────────────────────── */}
      <section id="work" className="py-24 bg-slate-950 text-white">
        <div ref={workRef} className={`mx-auto max-w-6xl px-6 ${fc(workVisible)}`}>
          <div className="mb-14 text-center">
            <p className="text-xs font-semibold tracking-[0.22em] text-amber-400 uppercase mb-3">Example First Draft</p>
            <h2 className="text-3xl md:text-[2.75rem] font-bold tracking-tight leading-[1.12]">
              This is where every project starts.
            </h2>
            <p className="mt-4 text-slate-400 max-w-2xl mx-auto text-base leading-relaxed">
              Before we finalize anything, we send every new client a first draft like this one. You explore it, tell us what you'd change — layout, colors, copy, features, anything — and we handle every revision until it's the exact site your church is looking for. Not a word or a decision required from you beyond your feedback.
            </p>
          </div>

          <div className="max-w-2xl mx-auto">
            {PROJECTS.map((project) => {
              const Wrapper = project.url ? 'a' : 'div';
              const wrapperProps = project.url ? { href: project.url, target: '_blank', rel: 'noopener noreferrer' } : {};
              return (
                <Wrapper key={project.title} {...wrapperProps}
                  className="group relative rounded-2xl overflow-hidden ring-1 ring-white/[0.06] bg-slate-900 hover:ring-amber-500/30 transition-all duration-500 hover:-translate-y-1 hover:shadow-2xl hover:shadow-amber-950/30">
                  {project.url && (
                    <div className="absolute top-3 right-3 z-10 flex items-center gap-1 rounded-full bg-amber-500/90 backdrop-blur-sm px-2.5 py-1 text-[10px] font-bold text-white tracking-wide">
                      <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" />
                      LIVE
                    </div>
                  )}
                  <div className="overflow-hidden aspect-video">
                    <img src={project.image} alt={project.title} loading="lazy"
                      className="w-full h-full object-cover opacity-75 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500" />
                  </div>
                  <div className="p-6">
                    <h3 className="font-semibold text-[15px] tracking-tight text-white mb-2">{project.title}</h3>
                    <p className="text-sm text-slate-400 leading-relaxed">{project.desc}</p>
                    {project.url && (
                      <div className="mt-4 flex items-center gap-1.5 text-amber-400 text-xs font-semibold">
                        Explore the example site <IconArrow className="w-3 h-3" />
                      </div>
                    )}
                  </div>
                </Wrapper>
              );
            })}
          </div>
          <div className="mt-12 text-center">
            <a href="#quote"
              className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-7 py-3.5 text-sm font-semibold text-white hover:bg-amber-600 active:scale-[0.97] transition-all duration-150 shadow-lg shadow-amber-950/40">
              Get a free quote for your church
              <IconArrow />
            </a>
          </div>
        </div>
      </section>

      {/* ── Process ────────────────────────────────────────────────────────── */}
      <section className="py-28 bg-[#f5f5f7]">
        <div ref={procRef} className={`mx-auto max-w-6xl px-6 ${fc(procVisible)}`}>
          <div className="text-center mb-16">
            <p className="text-xs font-semibold tracking-[0.22em] text-amber-600 uppercase mb-3">How it works</p>
            <h2 className="text-3xl md:text-[2.75rem] font-bold tracking-tight leading-[1.12]">
              Three steps from first call to live site.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {PROCESS_STEPS.map(({ step, title, time, desc }, i) => (
              <div key={step} className="relative bg-white rounded-3xl p-8 shadow-sm ring-1 ring-black/[0.05]">
                <div className="flex items-center justify-between mb-6">
                  <span className="text-xs font-bold tracking-[0.25em] text-amber-600">{step}</span>
                  {time && (
                    <span className="inline-flex items-center rounded-full bg-amber-50 border border-amber-100 px-2.5 py-1 text-[11px] font-semibold text-amber-700">
                      {time}
                    </span>
                  )}
                </div>
                <h3 className="text-xl font-bold tracking-tight mb-3">{title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{desc}</p>
                {i < PROCESS_STEPS.length - 1 && (
                  <div className="hidden md:flex absolute top-1/2 -right-2.5 -translate-y-1/2 z-10 h-5 w-5 items-center justify-center rounded-full bg-amber-500 text-white shadow-md">
                    <IconArrow className="w-2.5 h-2.5" />
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <a href="#quote"
              className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-7 py-3.5 text-sm font-semibold text-white hover:bg-amber-600 active:scale-[0.97] transition-all duration-150 shadow-md shadow-amber-900/15">
              Start your project today
              <IconArrow />
            </a>
          </div>
        </div>
      </section>

      {/* ── Pricing ────────────────────────────────────────────────────────── */}
      <section id="pricing" className="py-28 bg-white">
        <div ref={priceRef} className={`mx-auto max-w-6xl px-6 ${fc(priceVisible)}`}>
          <div className="text-center mb-14">
            <p className="text-xs font-semibold tracking-[0.22em] text-amber-600 uppercase mb-3">Pricing</p>
            <h2 className="text-3xl md:text-[2.75rem] font-bold tracking-tight leading-[1.12]">
              Simple, transparent pricing.<br className="hidden md:block" /> No surprises.
            </h2>
          </div>

          <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-6 items-start">
            <div className="relative rounded-3xl bg-slate-950 text-white ring-1 ring-amber-500/40 shadow-2xl shadow-slate-950/20 p-10">
              {/* Sale badge */}
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 whitespace-nowrap">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500 px-4 py-1.5 text-[11px] font-bold tracking-wide text-white uppercase shadow-lg shadow-amber-900/40">
                  ✦ Launch Offer 20% Off
                </span>
              </div>

              <div className="text-center mb-8 mt-2">
                <p className="text-[11px] font-semibold tracking-[0.2em] text-amber-400 uppercase mb-6">Everything Included</p>

                <div className="mb-5">
                  <p className="text-[10px] text-slate-500 uppercase tracking-widest mb-2">One-Time Build</p>
                  <div className="flex items-baseline justify-center gap-3">
                    <span className="text-slate-500 line-through text-2xl font-medium">$1,000</span>
                    <span className="text-6xl font-extrabold tracking-tight text-white">$800</span>
                  </div>
                  <p className="mt-1.5 text-amber-400 text-xs font-semibold">You save $200 on your build.</p>
                </div>

                <div className="inline-flex items-center gap-2 rounded-xl bg-white/[0.06] border border-white/[0.09] px-5 py-2.5">
                  <span className="text-slate-400 text-sm font-medium">+</span>
                  <span className="text-white text-xl font-bold">$30</span>
                  <span className="text-slate-400 text-sm">/ month hosting</span>
                </div>
              </div>

              <ul className="space-y-3 mb-8">
                {[
                  'We write all your copy. Not a word needed from you',
                  'Service times, location, and directions',
                  'Statement of faith and core beliefs page',
                  'We add your church to Google so people can find you',
                  'Online giving page linked to your existing methods',
                  'Live stream integration',
                  'Hosting, domain, and SSL fully managed by us',
                  'Completely hands free from start to launch',
                ].map((feat) => (
                  <li key={feat} className="flex items-center gap-2.5 text-sm">
                    <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-amber-500/20 text-amber-400">
                      <IconCheck className="w-2 h-2" />
                    </span>
                    <span className="text-slate-300">{feat}</span>
                  </li>
                ))}
              </ul>

              <a href="#quote"
                className="block text-center rounded-xl bg-amber-500 px-5 py-3.5 text-sm font-semibold text-white hover:bg-amber-600 active:scale-[0.97] transition-all duration-150 shadow-lg shadow-amber-900/30">
                Get Started for $800 Today
              </a>
              <p className="mt-3 text-center text-xs text-slate-600">
                Launch offer applies to the one-time build fee. Hosting billed at $30/month.
              </p>
            </div>

            {/* Need Something Bigger? */}
            <div className="rounded-3xl bg-slate-950 text-white ring-1 ring-amber-500/40 shadow-2xl shadow-slate-950/20 p-10 flex flex-col">
              <div className="flex-1">
                <p className="text-[11px] font-semibold tracking-[0.2em] text-amber-400 uppercase mb-6">Custom Projects</p>
                <h3 className="text-2xl font-bold tracking-tight text-white mb-4">Need Something Bigger?</h3>
                <p className="text-slate-400 text-sm leading-relaxed mb-8">
                  Need a full web app, mobile product, or something that doesn't fit a standard package? Reach out and we can have a call to talk through your project and put together pricing for a bigger product or app.
                </p>
                <ul className="space-y-3 mb-8">
                  {[
                    'Custom web applications',
                    'Mobile apps for iOS and Android',
                    'Automation and backend systems',
                    'Pricing tailored to your scope',
                  ].map((feat) => (
                    <li key={feat} className="flex items-center gap-2.5 text-sm">
                      <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-amber-500/20 text-amber-400">
                        <IconCheck className="w-2 h-2" />
                      </span>
                      <span className="text-slate-300">{feat}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <a href="#quote"
                className="block text-center rounded-xl bg-amber-500 px-5 py-3.5 text-sm font-semibold text-white hover:bg-amber-600 active:scale-[0.97] transition-all duration-150 shadow-lg shadow-amber-900/30">
                Schedule a Call →
              </a>
              <p className="mt-3 text-center text-xs text-slate-600">
                No commitment. Just a conversation about what you need.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ────────────────────────────────────────────────────────────── */}
      <section className="py-28 bg-[#f5f5f7]">
        <div ref={faqRef} className={`mx-auto max-w-2xl px-6 ${fc(faqVisible)}`}>
          <div className="text-center mb-14">
            <p className="text-xs font-semibold tracking-[0.22em] text-amber-600 uppercase mb-3">FAQ</p>
            <h2 className="text-3xl md:text-[2.75rem] font-bold tracking-tight">Common questions.</h2>
          </div>

          <div className="space-y-2">
            {FAQ_ITEMS.map(({ q, a }, i) => (
              <div key={q} className="rounded-2xl bg-white shadow-sm ring-1 ring-black/[0.05] overflow-hidden">
                <button
                  className="w-full flex items-center justify-between gap-4 px-7 py-5 text-left"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span className="text-[15px] font-semibold text-slate-900 leading-snug">{q}</span>
                  <span className={`shrink-0 transition-colors duration-200 ${openFaq === i ? 'text-amber-500' : 'text-slate-400'}`}>
                    <IconChevron open={openFaq === i} />
                  </span>
                </button>
                <div className={`overflow-hidden transition-[max-height,opacity] duration-300 ease-in-out ${openFaq === i ? 'max-h-72 opacity-100' : 'max-h-0 opacity-0'
                  }`}>
                  <p className="px-7 pb-6 text-sm text-slate-500 leading-relaxed">{a}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <a href="#quote"
              className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-7 py-3.5 text-sm font-semibold text-white hover:bg-amber-600 active:scale-[0.97] transition-all duration-150 shadow-md shadow-amber-900/15">
              Still have questions? Let's talk
              <IconArrow />
            </a>
          </div>
        </div>
      </section>

      {/* ── Guarantee ──────────────────────────────────────────────────────── */}
      <section className="py-28 bg-white">
        <div ref={guarRef} className={`mx-auto max-w-6xl px-6 ${fc(guarVisible)}`}>
          <div className="text-center mb-14">
            <p className="text-xs font-semibold tracking-[0.22em] text-amber-600 uppercase mb-3">Our Promise</p>
            <h2 className="text-3xl md:text-[2.75rem] font-bold tracking-tight leading-[1.12]">
              We don't stop until<br className="hidden md:block" /> your congregation is proud of it.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {GUARANTEES.map(({ Icon, title, desc }) => (
              <div key={title} className="text-center rounded-3xl bg-slate-50 ring-1 ring-slate-100 p-9">
                <div className="mx-auto mb-5 h-14 w-14 rounded-2xl bg-amber-100 ring-1 ring-amber-200/60 grid place-items-center text-amber-600">
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="text-[15px] font-bold tracking-tight text-slate-900 mb-2.5">{title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <a href="#quote"
              className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-7 py-3.5 text-sm font-semibold text-white hover:bg-amber-600 active:scale-[0.97] transition-all duration-150 shadow-md shadow-amber-900/15">
              I'm ready — get my free quote
              <IconArrow />
            </a>
          </div>
        </div>
      </section>

      {/* ── CTA banner ─────────────────────────────────────────────────────── */}
      <section className="bg-amber-500 py-14">
        <div ref={ctaRef} className={`mx-auto max-w-6xl px-6 flex flex-col md:flex-row items-center justify-between gap-6 ${fc(ctaVisible)}`}>
          <div>
            <p className="text-[11px] font-semibold tracking-[0.2em] text-amber-900/50 uppercase mb-1">Ready when you are</p>
            <h2 className="text-2xl md:text-3xl font-bold text-white tracking-tight">
              2–3 weeks. Starting at $800. Your church, online.
            </h2>
            <p className="mt-1 text-amber-900/60 text-sm font-medium">Completely hands free from first call to launch.</p>
          </div>
          <a href="#quote"
            className="shrink-0 inline-flex items-center gap-2 rounded-xl bg-white px-7 py-3.5 text-sm font-semibold text-amber-700 hover:bg-amber-50 active:scale-[0.97] transition-all duration-150 shadow-lg shadow-amber-900/20">
            Get a Free Quote
            <IconArrow />
          </a>
        </div>
      </section>

      {/* ── Quote form ─────────────────────────────────────────────────────── */}
      <section id="quote" className="relative overflow-hidden bg-slate-950 text-white py-28">
        <div className="pointer-events-none absolute left-1/2 top-0 -translate-x-1/2 h-80 w-[40rem] blur-3xl opacity-[0.07] bg-amber-400 rounded-full" />
        <div className="pointer-events-none absolute right-0 bottom-0 h-64 w-64 blur-3xl opacity-[0.05] bg-emerald-400 rounded-full" />

        <div className="relative mx-auto max-w-lg px-6">
          <div ref={formRef} className={fc(formVisible)}>
            <div className="text-center mb-12">
              <p className="text-xs font-semibold tracking-[0.28em] text-amber-400 uppercase mb-4">Free Quote</p>
              <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight">
                Let's build your<br />church's home online together.
              </h2>
              <p className="mt-4 text-slate-400 text-base leading-relaxed">
                No commitment, no pressure. Tell us about your congregation
                and we'll put together a plan.
              </p>
            </div>

            <form onSubmit={onSubmit} className="space-y-5">
              <input type="checkbox" name="botcheck" className="hidden" aria-hidden="true" />
              <input type="hidden" name="congregation_size" value={congSize || 'Not specified'} />

              <div className="grid sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-[10px] font-bold tracking-widest text-slate-500 uppercase">Church Name</span>
                  <input name="church_name" required placeholder="Your church name" autocomplete="organization"
                    className="mt-2 w-full rounded-xl bg-white/[0.05] border border-white/[0.09] px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-amber-500/70 focus:ring-1 focus:ring-amber-500/40 transition-all" />
                </label>
                <label className="block">
                  <span className="text-[10px] font-bold tracking-widest text-slate-500 uppercase">Your Name</span>
                  <input name="name" required placeholder="Your name" autocomplete="name"
                    className="mt-2 w-full rounded-xl bg-white/[0.05] border border-white/[0.09] px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-amber-500/70 focus:ring-1 focus:ring-amber-500/40 transition-all" />
                </label>
              </div>

              <label className="block">
                <span className="text-[10px] font-bold tracking-widest text-slate-500 uppercase">Email</span>
                <input name="email" type="email" required placeholder="you@yourchurch.org" autocomplete="email"
                  className="mt-2 w-full rounded-xl bg-white/[0.05] border border-white/[0.09] px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-amber-500/70 focus:ring-1 focus:ring-amber-500/40 transition-all" />
              </label>

              <label className="block">
                <span className="text-[10px] font-bold tracking-widest text-slate-500 uppercase">Phone <span className="normal-case tracking-normal font-normal text-slate-600">(optional)</span></span>
                <input name="phone" type="tel" autocomplete="tel" placeholder="(555) 000-0000"
                  className="mt-2 w-full rounded-xl bg-white/[0.05] border border-white/[0.09] px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-amber-500/70 focus:ring-1 focus:ring-amber-500/40 transition-all" />
              </label>

              <div>
                <span className="text-[10px] font-bold tracking-widest text-slate-500 uppercase">Congregation size</span>
                <div className="mt-2.5 flex flex-wrap gap-2">
                  {CONGREGATION_SIZES.map((opt) => (
                    <button key={opt} type="button"
                      onClick={() => setCongSize((p) => p === opt ? '' : opt)}
                      className={`text-xs font-medium px-3.5 py-1.5 rounded-lg border transition-all duration-200 ${congSize === opt
                        ? 'bg-amber-500 border-amber-500 text-white'
                        : 'bg-white/[0.05] border-white/[0.09] text-slate-400 hover:border-white/25 hover:text-slate-200'
                        }`}>
                      {opt}
                    </button>
                  ))}
                </div>
              </div>

              <label className="block">
                <span className="text-[10px] font-bold tracking-widest text-slate-500 uppercase">Tell us about your church</span>
                <textarea name="message" required rows={4}
                  placeholder="What does your church need most? Online giving, live streaming? Any questions at all?"
                  className="mt-2 w-full rounded-xl bg-white/[0.05] border border-white/[0.09] px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-amber-500/70 focus:ring-1 focus:ring-amber-500/40 transition-all resize-none" />
              </label>

              <button type="submit" disabled={isSubmitting}
                className="w-full rounded-xl bg-amber-500 px-5 py-3.5 text-sm font-semibold text-white hover:bg-amber-600 active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-150 shadow-lg shadow-amber-900/30">
                {isSubmitting ? 'Sending…' : 'Get My Free Quote →'}
              </button>

              {error && <p className="text-sm text-red-400 text-center bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-2">{error}</p>}
            </form>

            <div className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-slate-600">
              <a href="mailto:peronyxtech@gmail.com" className="hover:text-slate-300 transition-colors">
                peronyxtech@gmail.com
              </a>
              <span className="text-slate-700">·</span>
              <span>Mon to Fri, 8am to 6pm CT</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ─────────────────────────────────────────────────────────── */}
      <footer className="border-t border-slate-800 bg-slate-950 text-slate-400">
        <div className="mx-auto max-w-6xl px-6 py-10 flex flex-col md:flex-row items-center justify-between gap-6 text-sm">
          <div className="flex items-center gap-2.5">
            <svg viewBox="0 0 512 512" className="h-8 w-8 text-slate-200" fill="none" stroke="currentColor" strokeWidth={28} strokeLinecap="round" strokeLinejoin="round">
              <g transform="translate(256,256)">
                <circle cx="0" cy="0" r="140" />
                <path d="M -50 105 L -50 -105" />
                <path d="M -50 -105 L 10 -105 A 60 60 0 0 1 10 15 L -50 15" />
              </g>
            </svg>
            <span className="font-medium text-slate-200">Peronyx</span>
            <span className="text-slate-700">·</span>
            <span className="text-slate-500">Church & ministry websites.</span>
          </div>
          <span className="text-slate-600 text-xs">© 2026 Peronyx</span>
          <nav className="flex items-center gap-6">
            <a href="#pricing" className="hover:text-slate-200 transition-colors duration-150">Pricing</a>
            <a href="#features" className="hover:text-slate-200 transition-colors duration-150">Features</a>
            <a href="#quote" className="hover:text-slate-200 transition-colors duration-150">Get a Quote</a>
          </nav>
        </div>
      </footer>

      {/* ── Sticky floating CTA ────────────────────────────────────────────── */}
      <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 transition-all duration-500 ${showSticky ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-4 pointer-events-none'
        }`}>
        <a href="#quote"
          className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-6 py-3 text-sm font-semibold text-white shadow-2xl shadow-amber-900/50 hover:bg-amber-600 active:scale-[0.97] transition-all duration-150 ring-1 ring-amber-400/20">
          Get a Free Quote
          <IconArrow />
        </a>
      </div>

      {/* ── Thank-you modal ─────────────────────────────────────────────────── */}
      {showThankYou && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 backdrop-blur-sm backdrop-enter">
          <div className="mx-4 max-w-sm w-full rounded-2xl bg-white p-8 shadow-2xl text-center border border-slate-100 modal-enter">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 text-amber-600">
              <IconCheckCircle />
            </div>
            <h3 className="text-lg font-semibold text-slate-900">Quote request received</h3>
            <p className="mt-2 text-sm text-slate-500 leading-relaxed">
              Thank you for reaching out. We'll be in touch within one business day.
            </p>
            <button onClick={() => setShowThankYou(false)}
              className="mt-6 inline-flex items-center rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-medium text-white hover:bg-slate-800 active:scale-[0.97] transition-all duration-150">
              Close
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
